import type { IEventItemProps } from 'src/types/product';

import { useSelector } from 'react-redux';
import { useState, useEffect } from 'react';

import { Box, Stack, Button, Rating, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { fDate } from 'src/utils/format-time';

import { useAppDispatch } from 'src/context/store';
import { getTime, getAgeInterval } from 'src/utils';
import { parentLikeActivityMutationFunc } from 'src/context/like/parent_likeActivity';
import { parentUnlikeActivityMutationFunc } from 'src/context/like/parent_unlikeActivity';
import {
  PLACEHOLDER_ACTIVITY_DATE_ISO,
  PLACEHOLDER_ACTIVITY_END_TIME,
  PLACEHOLDER_ACTIVITY_START_TIME,
} from 'src/constants/activity';

import { Iconify } from 'src/components/iconify';
import { ActivityDetailsDialog } from 'src/components/activity-details-dialog';

// ----------------------------------------------------------------------

type Props = {
  product: IEventItemProps;
  detailsHref: string;
  entityType: string;
};

export function ProductItem({ product, detailsHref, entityType }: Props) {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { accessToken } = useSelector((state: any) => state.auth);
  const [hasAccessToken, setHasAccessToken] = useState(false);
  const [isLikedActivity, setIsLikedActivity] = useState(false);
  const [openAllDates, setOpenAllDates] = useState(false);
  const [selectedDates, setSelectedDates] = useState([] as any);
  const [openLocationsDialog, setOpenLocationsDialog] = useState(false);
  const [openDatesDialog, setOpenDatesDialog] = useState(false);
  const [openTimesDialog, setOpenTimesDialog] = useState(false);

  const rawActivities =
    (product as any)?.allActivities ??
    (product as any)?.activities ??
    [];
  const activityList = Array.isArray(rawActivities)
    ? rawActivities.filter((activity: any) => Boolean(activity))
    : [];

  const uniqueLocationIds = new Set(
    activityList
      .map((activity: any) => activity?.location?._id)
      .filter((id: string) => Boolean(id))
  );

  const uniqueStartDates = new Set(
    activityList
      .map((activity: any) => activity?.schedule?.startDate)
      .filter(
        (date: string) => date && date !== PLACEHOLDER_ACTIVITY_DATE_ISO
      )
  );

  const uniqueStartTimes = new Set(
    activityList
      .map((activity: any) => {
        const firstDay = activity?.schedule?.days?.[0];
        if (!firstDay) {
          return null;
        }
        const startTime = firstDay.startTime;
        const endTime = firstDay.endTime;
        if (
          startTime == null ||
          startTime === PLACEHOLDER_ACTIVITY_START_TIME
        ) {
          return null;
        }
        if (
          endTime == null ||
          endTime === PLACEHOLDER_ACTIVITY_END_TIME
        ) {
          return `${startTime}`;
        }
        return `${startTime}-${endTime}`;
      })
      .filter((time: string | null) => Boolean(time))
  );

  const hasMultipleLocations =
    Boolean((product as any)?.hasMultipleLocations) ||
    uniqueLocationIds.size > 1;
  const hasMultipleDates =
    Boolean((product as any)?.hasMultipleDates) ||
    uniqueStartDates.size > 1;
  const hasMultipleTimes =
    Boolean((product as any)?.hasMultipleTimes) ||
    uniqueStartTimes.size > 1;
  const hasValidPrimaryStartDate =
    Boolean(product.schedule?.startDate) &&
    product.schedule.startDate !== PLACEHOLDER_ACTIVITY_DATE_ISO;
  const scheduleWithValidDate = hasValidPrimaryStartDate
    ? product.schedule
    : activityList.find(
        (activity: any) =>
          activity?.schedule?.startDate &&
          activity.schedule.startDate !== PLACEHOLDER_ACTIVITY_DATE_ISO
      )?.schedule;

  const hasValidPrimaryStartTime =
    product?.schedule?.days?.[0]?.startTime != null &&
    product.schedule.days[0].startTime !== PLACEHOLDER_ACTIVITY_START_TIME;
  const scheduleWithValidTime = hasValidPrimaryStartTime
    ? product.schedule
    : activityList.find((activity: any) => {
        const startTime = activity?.schedule?.days?.[0]?.startTime;
        return (
          startTime != null && startTime !== PLACEHOLDER_ACTIVITY_START_TIME
        );
      })?.schedule;

  const dateLabel = scheduleWithValidDate?.startDate
    ? fDate(scheduleWithValidDate.startDate)
    : product.schedule?.startDate === PLACEHOLDER_ACTIVITY_DATE_ISO
      ? 'Tarih Sor'
      : '';
  const timeValue = scheduleWithValidTime?.days?.[0]?.startTime;
  const timeLabel =
    typeof timeValue === 'number' && timeValue === PLACEHOLDER_ACTIVITY_START_TIME
      ? ' - Saat Sor'
      : typeof timeValue === 'number' &&
          timeValue !== PLACEHOLDER_ACTIVITY_START_TIME
        ? ` - ${getTime(timeValue)}`
        : product?.schedule?.days?.[0]?.startTime === PLACEHOLDER_ACTIVITY_START_TIME
          ? ' - Saat Sor'
          : '';
  const dateTimeLabel = `${dateLabel}${timeLabel}`;
  const shouldShowDateRow =
    hasMultipleDates ||
    hasMultipleTimes ||
    (product.schedule?.days?.length ?? 0) > 1 ||
    Boolean(dateLabel);
  const dialogActivities = activityList.length > 0 ? activityList : [product];

  useEffect(() => {
    setHasAccessToken(accessToken);
  }, [accessToken]);

  useEffect(() => {
    setIsLikedActivity(product.likedByParent);
  }, [product]);

  const renderImage = () => (
    <Box
      sx={{
        position: 'relative',
        height: { xs: 132, sm: 240 },
        '&:hover': {
          '& img': {
            transition: 'all 0.5s ease',
            transform: 'scale(1.1)',
          },
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <img
          src={product.listing?.coverImage}
          alt={product.title}
          loading="lazy"
          width="100%"
          height="100%"
          style={{ objectFit: 'cover', cursor: 'pointer' }}
          onClick={() => detailsHref && router.push(detailsHref)}
        />
      </Box>

      <Button
        variant="outlined"
        onClick={() => {
          if (hasAccessToken) {
            if (isLikedActivity) {
              dispatch(
                parentUnlikeActivityMutationFunc({
                  activityId: product._id,
                })
              );
              setIsLikedActivity(false);
            } else {
              dispatch(
                parentLikeActivityMutationFunc({
                  activityId: product._id,
                })
              );
              setIsLikedActivity(true);
            }
          } else {
            router.push(paths.auth.jwt.signIn);
          }
        }}
        sx={{
          position: 'absolute',
          top: 32,
          right: 20,
          transition: 'all 0.5s ease',
          transform: 'translateY(-50%)',
          bgcolor: 'transparent',
          p: 0,
          minWidth: 0,
          width: 40,
          height: 40,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          zIndex: 9,
          color: 'common.white',
          borderColor: 'common.white',
        }}
      >
        <Iconify
          icon={
            isLikedActivity ? 'material-symbols-light:star' : 'material-symbols-light:star-outline'
          }
          width={32}
          sx={{ color: 'white' }}
        />
      </Button>
    </Box>
  );

  const renderContent = () => (
    <>
      <Box sx={{ pt: 1.25, px: 1.5 }}>
        <Typography
          variant="body1"
          fontWeight={600}
          fontFamily="Poppins"
          sx={{ mb: 1, color: 'primary.main', cursor: 'pointer' }}
          onClick={() => detailsHref && router.push(detailsHref)}
        >
          {product.title ?? (product as any)?.listing?.title}
        </Typography>
      </Box>

      <Stack
        direction={{ xs: 'row' }}
        alignItems={{ xs: 'flex-start' }}
        justifyContent="space-between"
        minHeight={100}
        pb={1.25}
        px={1.5}
      >
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{ mb: 0.5, visibility: product.listing?.rating?.average ? 'visible' : 'hidden' }}
          >
            <Rating
              value={product.listing?.rating?.average}
              readOnly
              size="small"
              precision={0.1}
              sx={{ fontSize: '12px' }}
              color="error"
            />
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{ mb: 0.5 }}
            visibility={!product.plus ? 'visible' : 'hidden'}
          >
            <Iconify
              icon="material-symbols:person-outline-rounded"
              width={20}
              sx={{ color: 'success.main' }}
            />

            <Typography variant="caption">
              {product?.listing?.ageInterval && product?.listing?.ageInterval.length > 0
                ? getAgeInterval(product?.listing?.ageInterval)
                : 'Tüm Yaşlar'}
            </Typography>
          </Stack>

          {product.isBlog && product?.schedule?.openHour && (
            <Stack direction="row" alignItems="center" gap={1}>
              <Iconify icon="tabler:clock" width={20} sx={{ color: 'warning.main' }} />
              <Typography
                variant="caption"
                fontFamily="Rubik"
                fontWeight={400}
                sx={{ color: 'text.secondary' }}
              >
                {product?.schedule?.closeHour
                  ? `${getTime(product?.schedule?.openHour)} - ${getTime(product?.schedule?.closeHour)}`
                  : getTime(product?.schedule?.openHour)}
              </Typography>
            </Stack>
          )}
          {product.plus && (
            <Stack sx={{ mb: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {product.listing?.description}
              </Typography>
            </Stack>
          )}
          {!product.plus && (
            <Stack
              direction="row"
              alignItems="center"
              gap={0.5}
              sx={{ mb: 0.5 }}
              visibility={shouldShowDateRow ? 'visible' : 'hidden'}
            >
              <Iconify icon="mdi:calendar-outline" width={20} sx={{ color: 'primary.main' }} />

              {hasMultipleDates ? (
                <Button
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto' }}
                  onClick={() => setOpenDatesDialog(true)}
                >
                  <Typography variant="caption">Tarihleri Gör</Typography>
                </Button>
              ) : product.schedule?.days && product.schedule?.days?.length > 1 ? (
                <Button
                  variant="text"
                  sx={{ p: 0 }}
                  onClick={() => {
                    setSelectedDates(product?.schedule?.days);
                    setOpenAllDates(true);
                  }}
                >
                  <Typography variant="caption">Tarihleri Gör</Typography>
                </Button>
              ) : hasMultipleTimes ? (
                <Button
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto' }}
                  onClick={() => setOpenTimesDialog(true)}
                >
                  <Typography variant="caption">Saatleri Gör</Typography>
                </Button>
              ) : (
                <Typography variant="caption">
                  {dateTimeLabel}
                </Typography>
              )}
            </Stack>
          )}
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{ mb: 0.5 }}
            visibility={
              product.listing?.format === 'online' ||
              product.location.district ||
              product.location.city ||
              hasMultipleLocations
                ? 'visible'
                : 'hidden'
            }
          >
            {product.listing.format === 'online' ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Iconify
                  icon="proicons:location"
                  width={20}
                  sx={{ color: 'error.main', visibility: 'hidden' }}
                />
                <Typography
                  variant="caption"
                  fontWeight={400}
                  fontFamily="Poppins"
                  sx={{
                    textAlign: 'right',
                    color: 'white',
                    bgcolor: 'success.main',
                    borderRadius: 0.5,
                    px: 1,
                    py: 0.5,
                  }}
                >
                  Online
                </Typography>
              </Box>
            ) : hasMultipleLocations ? (
              <>
                <Iconify icon="proicons:location" width={20} sx={{ color: 'error.main' }} />
                <Button
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto' }}
                  onClick={() => setOpenLocationsDialog(true)}
                >
                  <Typography variant="caption">Konumları Gör</Typography>
                </Button>
              </>
            ) : (
              <>
                <Iconify icon="proicons:location" width={20} sx={{ color: 'error.main' }} />
                <Typography variant="caption">
                  {(() => {
                    const title = product.location?.address?.title;
                    const district = product.location?.district;
                    const city = product.location?.city;
                    
                    const locationText = district === city
                      ? city
                      : district && city
                        ? `${district}/${city}`
                        : district || city || '';
                    
                    return title ? `${title} ${locationText}` : locationText;
                  })()}
                </Typography>
              </>
            )}
          </Stack>
        </Box>

        <Stack
          direction="column"
          alignItems="center"
          justifyContent="flex-end"
          gap={0.5}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            height: '100%',
          }}
        >
          {product.price ? (
            entityType === 'blog' ? null : (
              <Typography
                variant="body2"
                fontWeight={700}
                fontFamily="Poppins"
                sx={{ textAlign: 'right' }}
              >
                {`${product.price}₺`}
              </Typography>
            )
          ) : !product.plus && product.fee === 'free' ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'error.main',
                px: 1,
                py: 0.5,
                borderRadius: 0.5,
              }}
            >
              <Typography
                variant="caption"
                fontWeight={400}
                fontFamily="Poppins"
                sx={{ textAlign: 'right', color: 'white' }}
              >
                Ücretsiz
              </Typography>
            </Box>
          ) : null}
          <Button
            variant={product?.plus ? 'outlined' : 'contained'}
            size="small"
            color="primary"
            sx={{ mb: 0.25, px: 0.5, py: 0.5, borderRadius: 0.5, fontSize: '12px !important' }}
            onClick={() => detailsHref && router.push(detailsHref)}
          >
            {product?.plus ? 'Devamı' : 'TIKLA'}
          </Button>
        </Stack>
      </Stack>
    </>
  );

  return (
    <Stack
      sx={{
        borderRadius: 2,
        boxShadow: '0px 0px 10px 0px rgba(80, 80, 80, 0.5)',
        overflow: 'hidden',
      }}
    >
      {renderImage()}
      {renderContent()}

      {/* Multi-activity dialogs */}
      <ActivityDetailsDialog
        open={openLocationsDialog}
        onClose={() => setOpenLocationsDialog(false)}
        activities={dialogActivities}
        displayType="locations"
      />
      <ActivityDetailsDialog
        open={openDatesDialog}
        onClose={() => setOpenDatesDialog(false)}
        activities={dialogActivities}
        displayType="dates"
      />
      <ActivityDetailsDialog
        open={openTimesDialog}
        onClose={() => setOpenTimesDialog(false)}
        activities={dialogActivities}
        displayType="times"
      />

      {/* Single activity - multiple days dialog */}
      <ActivityDetailsDialog
        open={openAllDates}
        onClose={() => setOpenAllDates(false)}
        activities={
          selectedDates && selectedDates.length > 0 && selectedDates[0]?.date
            ? // Old format: days array with date field
              selectedDates
                .filter((date: any) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const dateToCheck = new Date(date.date);
                  dateToCheck.setHours(0, 0, 0, 0);
                  return dateToCheck >= today;
                })
                .map((date: any) => ({
                  ...product,
                  location: product.location,
                  schedule: {
                    ...product.schedule,
                    startDate: date.date,
                    days: [date],
                  },
                }))
            : product.schedule?.days && product.schedule.days.length > 1
              ? // New format: days array without date, use schedule.startDate
                (() => {
                  const startDate = new Date(product.schedule.startDate);
                  const activities: any[] = [];

                  product.schedule.days.forEach((day: any, idx: number) => {
                    const currentDate = new Date(startDate);
                    currentDate.setDate(startDate.getDate() + idx);

                    // Only show future dates
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    if (currentDate >= today) {
                      activities.push({
                        ...product,
                        location: product.location,
                        schedule: {
                          ...product.schedule,
                          startDate: currentDate.toISOString(),
                          days: [day],
                        },
                      });
                    }
                  });

                  return activities;
                })()
              : [product]
        }
        displayType="dates"
      />
    </Stack>
  );
}
